import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { adminFetch } from '../services/adminTransport'

const supabaseUrl = `${window.location.origin}/api`
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables')
}

/**
 * Singleton pattern to prevent multiple GoTrueClient instances
 * This is especially important for HMR (Hot Module Replacement) in development
 */
declare global {
  // eslint-disable-next-line no-var
  var __supabasePublicClient: SupabaseClient | undefined
}

/**
 * Get or create the public Supabase client
 * Optimized for high load with connection reuse and request deduplication
 *
 * Note: Storage key changed from v1 to v2 to fix session conflicts
 * This will require users to re-login but prevents auth issues
 */
function getSupabaseClient(): SupabaseClient {
  globalThis.__supabasePublicClient ??= createClient(supabaseUrl, supabaseKey, {
    auth: {
      storageKey: 'sb-sabai-tours-public-v2',
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
      flowType: 'pkce',
    },
    global: {
      fetch: adminFetch,
      headers: {
        'X-Client-Info': 'sabai-tours-public',
        'X-Client-Version': '1.0.0',
      },
    },
    db: {
      schema: 'public',
    },
    // Realtime disabled by default to reduce connections
    realtime: {
      params: {
        eventsPerSecond: 2,
      },
    },
  })
  return globalThis.__supabasePublicClient
}

/**
 * Public Supabase client for frontend operations
 */
export const supabase = getSupabaseClient()

/**
 * Compatibility alias. The same-origin gateway authorizes every request server-side.
 */
export const supabaseAdmin = supabase

export type Database = {
  public: {
    Tables: {
      tours: {
        Row: {
          id: string
          slug: string
          title: string
          description: string | null
          duration_days: number
          level: 'beginner' | 'intermediate' | 'expert'
          price_from_usd: number
          features: any
          gpx_url: string | null
          display_order: number
          image_settings?: any
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          slug: string
          title: string
          description?: string | null
          duration_days: number
          level: 'beginner' | 'intermediate' | 'expert'
          price_from_usd: number
          features?: any
          gpx_url?: string | null
          display_order?: number
          image_settings?: any
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          slug?: string
          title?: string
          description?: string | null
          duration_days?: number
          level?: 'beginner' | 'intermediate' | 'expert'
          price_from_usd?: number
          features?: any
          gpx_url?: string | null
          display_order?: number
          image_settings?: any
          created_at?: string
          updated_at?: string
        }
      }
      guides: {
        Row: {
          id: string
          name: string
          bio: string | null
          photo_url: string | null
          languages: string[]
          created_at: string
        }
        Insert: {
          id?: string
          name: string
          bio?: string | null
          photo_url?: string | null
          languages?: string[]
          created_at?: string
        }
        Update: {
          id?: string
          name?: string
          bio?: string | null
          photo_url?: string | null
          languages?: string[]
          created_at?: string
        }
      }
      leads: {
        Row: {
          id: string
          created_at: string
          name: string
          email: string
          phone: string | null
          party_size: number | null
          level: 'beginner' | 'intermediate' | 'expert' | null
          dates_preference: string | null
          gear_needed: boolean | null
          ride_style: 'single' | 'scenic' | 'mixed' | null
          height_cm: number | null
          weight_kg: number | null
          notes: string | null
          tour_slug: string | null
          status: 'new' | 'contacted' | 'won' | 'lost'
          scheduled_tour_id: string | null
          activity_slug: string | null
          source:
            | 'tour_booking'
            | 'activity_booking'
            | 'manual'
            | 'other'
            | null
        }
        Insert: {
          id?: string
          created_at?: string
          name: string
          email: string
          phone?: string | null
          party_size?: number | null
          level?: 'beginner' | 'intermediate' | 'expert' | null
          dates_preference?: string | null
          gear_needed?: boolean | null
          ride_style?: 'single' | 'scenic' | 'mixed' | null
          height_cm?: number | null
          weight_kg?: number | null
          notes?: string | null
          tour_slug?: string | null
          status?: 'new' | 'contacted' | 'won' | 'lost'
          scheduled_tour_id?: string | null
          activity_slug?: string | null
          source?:
            | 'tour_booking'
            | 'activity_booking'
            | 'manual'
            | 'other'
            | null
        }
        Update: {
          id?: string
          created_at?: string
          name?: string
          email?: string
          phone?: string | null
          party_size?: number | null
          level?: 'beginner' | 'intermediate' | 'expert' | null
          dates_preference?: string | null
          gear_needed?: boolean | null
          ride_style?: 'single' | 'scenic' | 'mixed' | null
          height_cm?: number | null
          weight_kg?: number | null
          notes?: string | null
          tour_slug?: string | null
          status?: 'new' | 'contacted' | 'won' | 'lost'
          scheduled_tour_id?: string | null
          activity_slug?: string | null
          source?:
            | 'tour_booking'
            | 'activity_booking'
            | 'manual'
            | 'other'
            | null
        }
      }
      tour_schedules: {
        Row: {
          id: string
          tour_id: string
          title: string
          recurring_pattern: 'weekly' | 'biweekly' | 'monthly' | 'custom'
          days_of_week: number[]
          start_date: string
          end_date: string | null
          max_capacity: number
          base_price: number
          is_active: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          tour_id: string
          title: string
          recurring_pattern: 'weekly' | 'biweekly' | 'monthly' | 'custom'
          days_of_week: number[]
          start_date: string
          end_date?: string | null
          max_capacity: number
          base_price: number
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          tour_id?: string
          title?: string
          recurring_pattern?: 'weekly' | 'biweekly' | 'monthly' | 'custom'
          days_of_week?: number[]
          start_date?: string
          end_date?: string | null
          max_capacity?: number
          base_price?: number
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      tour_instances: {
        Row: {
          id: string
          tour_schedule_id: string
          date: string
          start_time: string
          end_time: string | null
          max_capacity: number
          current_bookings: number
          price: number
          guide_id: string | null
          status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed'
          weather_status: 'good' | 'warning' | 'cancelled' | null
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          tour_schedule_id: string
          date: string
          start_time: string
          end_time?: string | null
          max_capacity: number
          current_bookings?: number
          price: number
          guide_id?: string | null
          status?: 'scheduled' | 'confirmed' | 'cancelled' | 'completed'
          weather_status?: 'good' | 'warning' | 'cancelled' | null
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          tour_schedule_id?: string
          date?: string
          start_time?: string
          end_time?: string | null
          max_capacity?: number
          current_bookings?: number
          price?: number
          guide_id?: string | null
          status?: 'scheduled' | 'confirmed' | 'cancelled' | 'completed'
          weather_status?: 'good' | 'warning' | 'cancelled' | null
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      bookings: {
        Row: {
          id: string
          tour_instance_id: string
          lead_id: string
          booking_date: string
          status:
            | 'pending'
            | 'confirmed'
            | 'paid'
            | 'completed'
            | 'cancelled'
            | 'no_show'
          party_size: number
          total_price: number
          deposit_paid: number | null
          balance_due: number | null
          payment_status: 'pending' | 'deposit' | 'full' | 'refunded'
          special_requirements: string | null
          confirmation_code: string
          voucher_url: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          tour_instance_id: string
          lead_id: string
          booking_date?: string
          status?:
            | 'pending'
            | 'confirmed'
            | 'paid'
            | 'completed'
            | 'cancelled'
            | 'no_show'
          party_size: number
          total_price: number
          deposit_paid?: number | null
          balance_due?: number | null
          payment_status?: 'pending' | 'deposit' | 'full' | 'refunded'
          special_requirements?: string | null
          confirmation_code?: string
          voucher_url?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          tour_instance_id?: string
          lead_id?: string
          booking_date?: string
          status?:
            | 'pending'
            | 'confirmed'
            | 'paid'
            | 'completed'
            | 'cancelled'
            | 'no_show'
          party_size?: number
          total_price?: number
          deposit_paid?: number | null
          balance_due?: number | null
          payment_status?: 'pending' | 'deposit' | 'full' | 'refunded'
          special_requirements?: string | null
          confirmation_code?: string
          voucher_url?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      equipment: {
        Row: {
          id: string
          name: string
          description: string | null
          quantity: number
          status: 'available' | 'maintenance' | 'retired'
          purchase_date: string | null
          maintenance_notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          description?: string | null
          quantity?: number
          status?: 'available' | 'maintenance' | 'retired'
          purchase_date?: string | null
          maintenance_notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          description?: string | null
          quantity?: number
          status?: 'available' | 'maintenance' | 'retired'
          purchase_date?: string | null
          maintenance_notes?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      tour_equipment: {
        Row: {
          id: string
          tour_instance_id: string
          equipment_id: string
          quantity_needed: number
          quantity_assigned: number
          notes: string | null
          created_at: string
        }
        Insert: {
          id?: string
          tour_instance_id: string
          equipment_id: string
          quantity_needed?: number
          quantity_assigned?: number
          notes?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          tour_instance_id?: string
          equipment_id?: string
          quantity_needed?: number
          quantity_assigned?: number
          notes?: string | null
          created_at?: string
        }
      }
      site_settings: {
        Row: {
          id: string
          key: string
          value: any
          description: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          key: string
          value?: any
          description?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          key?: string
          value?: any
          description?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      partners: {
        Row: {
          id: string
          slug: string
          name: string
          partner_type: 'website' | 'facebook'
          url: string
          description: string | null
          logo_url: string | null
          preview_image_url: string | null
          preview_source: 'auto' | 'manual'
          display_order: number
          is_active: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          slug: string
          name: string
          partner_type: 'website' | 'facebook'
          url: string
          description?: string | null
          logo_url?: string | null
          preview_image_url?: string | null
          preview_source?: 'auto' | 'manual'
          display_order?: number
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          slug?: string
          name?: string
          partner_type?: 'website' | 'facebook'
          url?: string
          description?: string | null
          logo_url?: string | null
          preview_image_url?: string | null
          preview_source?: 'auto' | 'manual'
          display_order?: number
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
      }
    }
  }
}
